import React from 'react';
import { ArrowRight, CheckCircle2, Brain, Target, Heart, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';
import FAQ from '../components/FAQ';
import { faqMap } from '../data/faqContent';

const HypnosePertePoids = () => {
  return (
    <>
      <SEO 
        title="Hypnose pour maigrir à Vevey & Clarens | Hypnopraxie"
        description="Hypnose et hypnopraxie pour maigrir à Vevey et Clarens (Montreux). Consultations en cabinet médical pour traiter les compulsions alimentaires et le stress."
        keywords="hypnose Vevey, hypnose Clarens, hypnopraxie, hypnose perte de poids, compulsions alimentaires Vevey, hypnothérapie Montreux, hypnose maigrir Riviera"
        url="https://nutricentreriviera.ch/hypnose-perte-de-poids-vevey-clarens"
      />
      
      <div style={{ marginTop: '100px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--gradient-hero)',
          padding: '4rem 0',
          textAlign: 'center'
        }}>
          <div className="container">
            <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
              Hypnose pour maigrir à Vevey & Clarens
            </h1>
            <p className="heading-subtitle" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
              Une méthode efficace pour agir sur les comportements alimentaires et les blocages inconscients
            </p>
            <a 
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Prendre rendez-vous
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        {/* Main Content */}
        <section className="section" style={{ background: 'white' }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            {/* Introduction */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem', color: 'var(--accent-primary)' }}>
                L'hypnose : une méthode efficace pour maigrir durablement
              </h2>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                L'<strong>hypnose (hypnopraxie)</strong> est une méthode efficace pour agir sur les comportements alimentaires 
                et les blocages inconscients.
              </p>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                Je propose des consultations à <strong>Vevey</strong> et à <strong>Clarens (Montreux)</strong>, 
                dans un cadre professionnel au sein d'un cabinet médical.
              </p>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                L'accompagnement permet de travailler sur les causes profondes et combine <strong>hypnose et nutrition</strong> 
                pour des résultats durables.
              </p>
            </div>

            {/* Les causes profondes */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Travailler sur les causes profondes
              </h2>
              <div style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--border-light)'
              }}>
                <p className="body-large" style={{ marginBottom: '1.5rem' }}>
                  L'hypnose permet de travailler sur les causes profondes de la prise de poids :
                </p>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Stress</strong> — Anxiété et tension qui favorisent la prise de poids</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Compulsions alimentaires</strong> — Difficultés à respecter un plan nutritionnel</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Grignotage émotionnel</strong> — Manger en réponse aux émotions</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Perte de contrôle</strong> — Sabotages inconscients qui empêchent la perte de poids</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pour qui ? */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Une approche particulièrement adaptée
              </h2>
              <div style={{
                background: 'linear-gradient(135deg, var(--accent-primary-light) 0%, rgba(200, 230, 216, 0.3) 100%)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--accent-primary)',
                borderLeft: '4px solid var(--accent-primary)'
              }}>
                <p className="body-large" style={{ lineHeight: '1.8', marginBottom: '1rem' }}>
                  L'hypnose est <strong>particulièrement adaptée aux personnes ayant essayé plusieurs méthodes sans succès</strong>.
                </p>
                <p className="body-large" style={{ lineHeight: '1.8' }}>
                  Si vous avez déjà tenté plusieurs régimes sans résultats durables, l'hypnose peut débloquer 
                  les mécanismes inconscients qui sabotent vos efforts.
                </p>
              </div>
            </div>

            {/* L'approche combinée */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Nutrition + Hypnothérapie : une synergie efficace
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '2rem'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }}
                className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <Brain size={24} style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    Hypnose (Hypnopraxie)
                  </h3>
                  <p className="body-medium" style={{ lineHeight: '1.6' }}>
                    Travail sur les comportements automatiques, les compulsions, le stress et les blocages émotionnels liés à l'alimentation.
                  </p>
                </div>

                <div style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }}
                className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <Target size={24} style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    Nutrition personnalisée
                  </h3>
                  <p className="body-medium" style={{ lineHeight: '1.6' }}>
                    Plan nutritionnel adapté à vos besoins métaboliques, vos hormones et votre mode de vie pour des résultats durables.
                  </p>
                </div>
              </div>
            </div>

            {/* Consultations */}
            <div style={{ marginBottom: '3rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Consultations à Vevey, Clarens et en ligne
              </h2>
              <p className="body-large" style={{ marginBottom: '1.5rem', lineHeight: '1.8' }}>
                Les séances d'hypnose pour maigrir sont disponibles en présentiel à <strong>Vevey</strong> et <strong>Clarens (Montreux)</strong> 
                ou en ligne pour plus de flexibilité.
              </p>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                L'hypnose en ligne est tout aussi efficace et permet un accompagnement régulier sans contrainte de déplacement.
              </p>
              
              {/* Localisation */}
              <p className="body-large" style={{ 
                marginTop: '2rem', 
                padding: '1rem',
                background: 'var(--bg-page)',
                borderRadius: '0.75rem',
                fontWeight: 500,
                textAlign: 'center',
                borderLeft: '4px solid var(--accent-primary)'
              }}>
                Cabinet à Vevey et consultations à Clarens (Montreux), facilement accessible depuis toute la Riviera.
              </p>
            </div>
          </div>
        </section>

        <FAQ items={faqMap['hypnose-perte-de-poids-vevey-clarens']} />

        <RelatedPages items={relatedPagesMap['hypnose-perte-de-poids-vevey-clarens']} />

        {/* CTA Section */}
        <section style={{
          background: 'var(--accent-primary)',
          color: 'white',
          padding: '5rem 0'
        }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
              Prêt(e) à retrouver une relation saine avec l'alimentation ?
            </h2>
            <p className="body-large" style={{ 
              color: 'white', 
              opacity: 0.95, 
              maxWidth: '700px', 
              margin: '0 auto 2.5rem' 
            }}>
              Prenez rendez-vous pour une première séance d'hypnose à Vevey ou Clarens
            </p>
            <a
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary" 
              style={{ 
                background: 'white', 
                color: 'var(--accent-primary)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                textDecoration: 'none'
              }}
            >
              Réserver une consultation
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        <style>{`
          .approach-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(61, 84, 70, 0.15);
            border-color: var(--accent-primary);
          }
        `}</style>
      </div>
    </>
  );
};

export default HypnosePertePoids;
